// Mirrors the tweak catalog in the flowtweaks-app desktop repo (data/tweaks/*.ts) — customer-facing
// fields only (id, name, whatItChanges, whyItMayHelp, riskLevel, requiresRestart, reversible).
// Kept in sync by hand when the app's tweak copy changes; there is no shared package between the
// two repos today. fpsImpact is deliberately omitted — same as the app's own UI, it's an internal
// FPS Predictor weighting only, never shown to the customer as a claim.

export const CATEGORY_LABELS = {
  "fps-latency": "FPS & Latency",
  input: "Input",
  gpu: "GPU",
  "windows-performance": "Windows Performance",
  network: "Network",
  "background-processes": "Background Processes",
  "storage-cleanup": "Storage Cleanup",
  personalization: "Personalization",
  "privacy-noise": "Privacy & Noise",
};

export const CATEGORY_ORDER = [
  "fps-latency",
  "input",
  "gpu",
  "windows-performance",
  "network",
  "background-processes",
  "storage-cleanup",
  "personalization",
  "privacy-noise",
];

export const TWEAKS = [
  // FPS & Latency
  {
    id: "game-mode",
    name: "Game Mode",
    category: "fps-latency",
    whatItChanges: "Turns on Windows Game Mode, which deprioritizes background tasks in favor of the game in focus.",
    whyItMayHelp: "Game Mode can reduce background interruptions and stutters while a game is running.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: true,
  },
  {
    id: "scheduling-registry-tweaks",
    name: "CPU & Network Scheduling",
    category: "fps-latency",
    whatItChanges:
      "Raises the scheduling priority Windows gives to games, and tunes network throttling settings used for multimedia/game traffic.",
    whyItMayHelp:
      "These are the same low-level scheduler values Windows itself uses to favor foreground games — raising them can reduce input lag and stutter under load.",
    riskLevel: "medium",
    requiresRestart: true,
    reversible: true,
  },
  {
    id: "fullscreen-optimizations",
    name: "Disable Fullscreen Optimizations",
    category: "fps-latency",
    whatItChanges: "Turns off Windows Fullscreen Optimizations so games run in true exclusive fullscreen when they request it.",
    whyItMayHelp:
      "Microsoft's own data shows FSO performs about as well as exclusive fullscreen for most people on modern titles — this mainly helps older DX9/DX11 games. Harmless to leave on, worth trying if a specific game feels laggy in fullscreen.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: true,
  },
  // Input
  {
    id: "disable-mouse-acceleration",
    name: "Disable Mouse Acceleration",
    category: "input",
    whatItChanges:
      'Turns off Windows\' "Enhance Pointer Precision" mouse acceleration curve, so cursor movement is a flat, consistent ratio to physical mouse movement.',
    whyItMayHelp:
      "Acceleration means the same physical swipe moves the cursor a different distance depending on speed — competitive shooters rely on consistent, repeatable aim, which acceleration actively works against. This is about aim consistency, not frame rate.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: true,
  },
  {
    id: "disable-accessibility-shortcuts",
    name: "Disable Accidental Accessibility Shortcuts",
    category: "input",
    whatItChanges:
      "Turns off the hold-a-key-for-several-seconds hotkeys that pop up the StickyKeys, FilterKeys, and ToggleKeys prompts — the accessibility features themselves stay available in Settings if you want them.",
    whyItMayHelp:
      "Competitive binds often spam Shift (crouch/walk) or Num Lock — exactly the keys those hotkeys watch for — so a dialog can steal focus mid-fight. This only removes the accidental trigger, not the features.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: true,
  },
  // GPU
  {
    id: "nvidia-disable-telemetry",
    name: "Disable NVIDIA Telemetry",
    category: "gpu",
    whatItChanges: "Opts this PC out of NVIDIA's driver telemetry collection.",
    whyItMayHelp: "One less background data-collection process reporting home — a privacy opt-out, not a performance change.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: true,
  },
  {
    id: "nvidia-disable-telemetry-container",
    name: "Disable NVIDIA Telemetry Container",
    category: "gpu",
    whatItChanges: "Stops and disables the NVIDIA Telemetry Container service, the background process that actually uploads driver telemetry.",
    whyItMayHelp:
      "The registry opt-out stops new data from being collected — this removes the background service that does the uploading, closing the loop.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: true,
  },
  {
    id: "hags",
    name: "Hardware-Accelerated GPU Scheduling",
    category: "gpu",
    whatItChanges: "Turns on HAGS, letting your GPU manage its own memory queue instead of relying entirely on Windows.",
    whyItMayHelp:
      "On supported drivers, HAGS can reduce latency by cutting out a layer of CPU-side scheduling overhead. Typical gain is small (frame pacing / 1% lows more than raw FPS) and most noticeable if your CPU is the bottleneck, not your GPU — it needs a fairly recent GPU generation to even be supported, and can cost up to ~1GB of extra VRAM. The app checks your specific GPU and tells you whether it's actually a good idea on your hardware.",
    riskLevel: "medium",
    requiresRestart: true,
    reversible: true,
  },
  {
    id: "amd-disable-radeon-chill",
    name: "Disable Radeon Chill",
    category: "gpu",
    whatItChanges: "Turns off Radeon Chill, which otherwise dynamically caps your frame rate during low on-screen motion to save power.",
    whyItMayHelp:
      "Chill's whole job is capping FPS below your max whenever it thinks little is moving — exactly the moments competitive play needs full frame rate ready (holding an angle, tracking a distant target). Turning it off keeps FPS uncapped at all times, and is also required for Anti-Lag to function (the two are mutually exclusive at the driver level).",
    riskLevel: "medium",
    requiresRestart: false,
    reversible: true,
  },
  {
    id: "amd-enable-radeon-boost",
    name: "Enable Radeon Boost",
    category: "gpu",
    whatItChanges:
      "Turns on Radeon Boost, which briefly lowers render resolution during fast camera motion to raise frame rate, then restores it once motion settles.",
    whyItMayHelp:
      "The FPS gain during fast motion is real and measurable, but so is the resolution drop that causes it — several sources specifically recommend leaving this OFF for ranked/competitive play, since target clarity matters most in exactly the fast-motion moments Boost targets. Better fit for casual or GPU-limited sessions than competitive matches.",
    riskLevel: "medium",
    requiresRestart: false,
    reversible: true,
  },
  {
    id: "amd-enable-anti-lag",
    name: "Enable Radeon Anti-Lag",
    category: "gpu",
    whatItChanges:
      "Turns on Radeon Anti-Lag (the classic mechanism, not Anti-Lag Deluxe), which paces the CPU so it doesn't queue frames too far ahead of what the GPU can render.",
    whyItMayHelp:
      "Same category of fix as NVIDIA Reflex — it directly targets input latency rather than raw frame rate, which matters a lot for how responsive aiming feels even when the FPS counter doesn't move much. The best-evidenced tweak in the GPU category.",
    riskLevel: "medium",
    requiresRestart: false,
    reversible: true,
  },
  {
    id: "amd-disable-ulps",
    name: "Disable ULPS",
    category: "gpu",
    whatItChanges: "Turns off ULPS (Ultra Low Power State), which otherwise aggressively powers down parts of the GPU when AMD's driver thinks it's idle.",
    whyItMayHelp:
      "ULPS transitions are a known, documented source of stutter on multi-GPU/CrossFire setups specifically — the evidence for single-GPU systems is thinner and contested. The app checks how many GPUs your PC actually has and tells you whether it's worth applying.",
    riskLevel: "medium",
    requiresRestart: true,
    reversible: true,
  },
  // Windows Performance
  //
  // Note: Disable Memory Integrity was removed from the catalog entirely. Research found Riot
  // Vanguard (Valorant's anti-cheat) hard-requires Memory Integrity/VBS to be on — disabling it
  // breaks Valorant outright (error VAN9005), not a performance/security tradeoff. Anyone who
  // applied it before this fix can turn it back on via Windows Security > Device Security > Core
  // Isolation > Memory Integrity (toggle on, restart).
  {
    id: "ultimate-performance-power-plan",
    name: "Ultimate Performance Power Plan",
    category: "windows-performance",
    whatItChanges: 'Adds and activates the hidden "Ultimate Performance" Windows power plan, which removes most power-saving throttling.',
    whyItMayHelp:
      "Prevents Windows from parking CPU cores or stepping down clocks to save power, which matters most on desktops and plugged-in laptops. The gap versus regular High Performance is small — most of the value is over the Balanced plan.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: true,
  },
  {
    id: "disable-fast-startup",
    name: "Disable Fast Startup",
    category: "windows-performance",
    whatItChanges: "Turns off Fast Startup (hybrid boot), so shutdown does a full shutdown instead of hibernating the kernel session.",
    whyItMayHelp:
      "Fast Startup can leave stale driver/network state behind between sessions; a full shutdown avoids that at the cost of a few extra boot seconds. Not a performance tweak — a stability one.",
    riskLevel: "low",
    requiresRestart: true,
    reversible: true,
  },
  {
    id: "visual-effects",
    name: "Visual Effects",
    category: "windows-performance",
    whatItChanges: 'Sets Windows visual effects to "Adjust for best performance," keeping only font smoothing, window drag preview, and thumbnails.',
    whyItMayHelp:
      "Fewer animations and transparency effects mean less GPU/CPU time spent on desktop compositing. On a PC with a dedicated GPU this mostly affects desktop responsiveness, not in-game FPS — it matters more on integrated-graphics/lower-end hardware.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: true,
  },
  {
    id: "disable-hibernation",
    name: "Disable Hibernation",
    category: "windows-performance",
    whatItChanges: "Turns off Windows Hibernation, removing it from the power menu. Desktop PCs only — never offered on laptops.",
    whyItMayHelp:
      "Hibernation keeps a large reserved hiberfil.sys on your boot drive and adds a background task most desktop players never use.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: true,
  },
  {
    id: "disable-usb-selective-suspend",
    name: "Disable USB Selective Suspend",
    category: "windows-performance",
    whatItChanges:
      "Turns off USB Selective Suspend (covers root-hub idle power-down too) on both the plugged-in and battery power profiles, so USB devices are never told to idle down to save power.",
    whyItMayHelp:
      "A wired mouse or keyboard waking from a suspended USB hub adds a small, inconsistent delay right when you move — this removes that source of micro-stutter.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: false,
  },
  {
    id: "core-parking-disable",
    name: "Disable Core Parking",
    category: "windows-performance",
    whatItChanges: "Sets the minimum parked-core count to 0 on the active power plan, so Windows never idles CPU cores into a deep power-saving state. Desktop PCs only.",
    whyItMayHelp:
      "Unparking a core the moment a game's main thread needs it costs 1-5ms — enough to show up as an occasional frame-time hitch, not a lower average FPS.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: false,
  },
  {
    id: "nvme-power-management",
    name: "NVMe Power Management Tuning",
    category: "windows-performance",
    whatItChanges:
      "Raises NVMe idle-timeout and transition-latency-tolerance thresholds, so a fast drive doesn't drop into a deep low-power state during brief idle gaps.",
    whyItMayHelp:
      "Waking an NVMe drive from a deep power state adds real I/O latency — mostly noticeable as an occasional stall during texture streaming or level loads.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: false,
  },
  // Network
  {
    id: "dns-cloudflare",
    name: "Cloudflare DNS",
    category: "network",
    whatItChanges: "Sets your network adapter's DNS servers to Cloudflare (1.1.1.1 / 1.0.0.1).",
    whyItMayHelp:
      "Cloudflare's resolver is typically faster than an ISP default, which can shave time off game server/matchmaking lookups — it does not affect in-game ping once a match has started.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: true,
  },
  {
    id: "delivery-optimization-http-only",
    name: "Delivery Optimization: Microsoft Only",
    category: "network",
    whatItChanges:
      "Sets Windows Update Delivery Optimization to download only from Microsoft, instead of also uploading to and downloading from other PCs (peer-to-peer).",
    whyItMayHelp:
      "Peer-to-peer update sharing can saturate upload bandwidth in the background without warning — this stops that traffic entirely while updates still download normally.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: true,
  },
  // Background Processes
  {
    id: "disable-sysmain",
    name: "Disable SysMain (Superfetch)",
    category: "background-processes",
    whatItChanges: "Stops and disables the SysMain service, which preloads apps into RAM based on usage patterns.",
    whyItMayHelp:
      "On SSD/NVMe systems, SysMain's background disk activity mostly just competes with the game for resources. If your game drive is a spinning HDD, SysMain's preloading is doing real work there — worth skipping this one on HDD-primary setups.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: true,
  },
  {
    id: "disable-webclient",
    name: "Disable WebClient (WebDAV)",
    category: "background-processes",
    whatItChanges: "Stops and disables the WebClient service, which lets Windows browse web servers (WebDAV shares) like local folders.",
    whyItMayHelp: "Almost nobody uses WebDAV network shares day to day — this is one more background service that never needs to run on a gaming PC.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: true,
  },
  {
    id: "disable-gamedvr",
    name: "Disable Game Bar Background Capture",
    category: "background-processes",
    whatItChanges:
      "Turns off Xbox Game Bar's background capture buffer (GameDVR) and the app-capture pipeline it uses — separate from just hiding the Game Bar overlay.",
    whyItMayHelp:
      "The always-on recording buffer that powers \"recent highlights\" runs a real background capture pipeline even when you never open Game Bar. Disabling it removes that overhead entirely.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: true,
  },
  {
    id: "nic-power-management",
    name: "Disable Network Adapter Power Management",
    category: "background-processes",
    whatItChanges: "Turns off power management across every network adapter, so Windows never idles a NIC down to save power.",
    whyItMayHelp:
      "An idled adapter waking on traffic can introduce a small, inconsistent latency spike right when a packet arrives — separate from USB Selective Suspend, this targets the network adapter's own power-down behavior.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: false,
  },
  // Storage Cleanup
  {
    id: "clear-temp-files",
    name: "Clear Temp Files",
    category: "storage-cleanup",
    whatItChanges: "Deletes files in %TEMP% and C:\\Windows\\Temp. Files currently in use are skipped automatically.",
    whyItMayHelp:
      "Frees up disk space and clears out stale temp data that can accumulate over time. Windows\\Prefetch is deliberately left alone — Prefetch files are themselves an optimization Windows builds to speed up app/game launches, and wiping them can slow down the next few launches instead of helping.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: false,
  },
  {
    id: "enable-ssd-trim",
    name: "Enable SSD TRIM",
    category: "storage-cleanup",
    whatItChanges: "Makes sure Windows sends TRIM notifications to your drive when files are deleted, so the SSD knows which blocks are actually free.",
    whyItMayHelp:
      "Without TRIM, a drive can't reclaim deleted blocks ahead of time, which shows up as slower writes as it fills up. This is already the Windows default on virtually every modern install — this just confirms and corrects it if something turned it off.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: true,
  },
  {
    id: "optimize-drives-retrim",
    name: "Optimize Drives (Retrim)",
    category: "storage-cleanup",
    whatItChanges:
      "Runs Windows' Optimize Drives in retrim-only mode (defrag /L) across every drive — the SSD-safe pass, never a traditional block-shuffling defrag.",
    whyItMayHelp:
      "This is the same maintenance pass Windows' own weekly Optimize Drives schedule runs — doing it on demand catches a drive up immediately instead of waiting for the next scheduled run.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: false,
  },
  {
    id: "disk-cleanup",
    name: "Disk Cleanup",
    category: "storage-cleanup",
    whatItChanges: "Configures and runs Windows' built-in Disk Cleanup tool (cleanmgr) across system caches, old installs, and error report queues.",
    whyItMayHelp:
      "Reclaims disk space Windows itself accumulates over time — old update files, memory dumps, error report archives. The speed benefit is conditional: it mainly shows up on a drive that's getting full, not one with plenty of free space.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: false,
  },
  // Personalization
  {
    id: "dark-mode",
    name: "Dark Mode",
    category: "personalization",
    whatItChanges: "Switches Windows apps and system UI to dark theme.",
    whyItMayHelp: "Not a performance change — purely cosmetic preference, included because it's part of the standard setup pass.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: true,
  },
  {
    id: "disable-window-animations",
    name: "Disable Window Animations",
    category: "personalization",
    whatItChanges: "Turns off window minimize/maximize/restore animations specifically, without touching any of the other Visual Effects settings.",
    whyItMayHelp:
      "For anyone who wants Visual Effects left on its default mix but still finds window-open/close animations adding a beat of delay before a window is usable, this is the one setting on its own.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: true,
  },
  // Privacy & Noise
  {
    id: "deny-nearby-sharing",
    name: "Disable Nearby Sharing",
    category: "privacy-noise",
    whatItChanges: "Turns off Nearby Sharing, which otherwise has this PC discoverable over Bluetooth/Wi-Fi Direct to receive files from nearby devices.",
    whyItMayHelp: "One less radio-scanning background feature running while you play, and one less way this PC shows up as discoverable to strangers nearby.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: true,
  },
  {
    id: "disable-sensors",
    name: "Disable Sensors",
    category: "privacy-noise",
    whatItChanges: "Turns off Windows' generic sensor platform (ambient light, accelerometer, and similar sensors on hardware that has them).",
    whyItMayHelp: "A desktop or gaming laptop rarely has anything meaningfully using these sensors — one less background polling loop.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: true,
  },
  {
    id: "disable-location",
    name: "Disable Location Services",
    category: "privacy-noise",
    whatItChanges: "Turns off Windows Location Services system-wide, so no app or background service can query this PC's location.",
    whyItMayHelp: "Removes background location polling entirely — most desktop gaming setups have no real use for it.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: true,
  },
  {
    id: "disable-voice-activation",
    name: "Disable App Voice Activation",
    category: "privacy-noise",
    whatItChanges: 'Turns off "Allow apps to use voice activation," including while the PC is locked, so apps can\'t listen for wake words in the background.',
    whyItMayHelp: "Removes an always-listening background audio process most competitive players never intentionally use.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: true,
  },
  {
    id: "disable-input-personalization",
    name: "Disable Typing & Ink Personalization",
    category: "privacy-noise",
    whatItChanges: "Stops Windows from collecting your typing and handwriting patterns to personalize its dictionary and suggestions.",
    whyItMayHelp: "Removes a background text-collection process — purely a data-collection opt-out, no effect on how typing itself works.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: true,
  },
  {
    id: "reduce-telemetry",
    name: "Reduce Diagnostic Data",
    category: "privacy-noise",
    whatItChanges:
      'Sets Windows diagnostic data collection to the lowest level this edition allows (Home/Pro can\'t go below "Required"; only Enterprise/Education can select "Security").',
    whyItMayHelp: "Less background diagnostic upload traffic — a real but modest reduction in one more thing quietly using the network and disk.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: true,
  },
  {
    id: "disable-feedback-prompts",
    name: "Disable Feedback Prompts",
    category: "privacy-noise",
    whatItChanges: 'Stops Windows from periodically popping up "How is this working for you?" feedback requests.',
    whyItMayHelp: "One less interruption competing for focus — purely a notification opt-out, not a privacy or performance change.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: true,
  },
  {
    id: "disable-experimentation",
    name: "Disable Microsoft Experimentation",
    category: "privacy-noise",
    whatItChanges: "Blocks Microsoft from silently A/B-testing Windows Settings changes on this PC in the background.",
    whyItMayHelp:
      "These experiments can change how Settings behaves without warning, mid-session, for reasons that have nothing to do with your setup — worth noting this also blocks Known Issue Rollback, Microsoft's mechanism for silently un-shipping a broken update fix, so a bad fix may linger slightly longer than normal in exchange.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: true,
  },
  {
    id: "disable-settings-sync",
    name: "Disable Settings Sync",
    category: "privacy-noise",
    whatItChanges: "Turns off Windows syncing your settings (theme, passwords, language, etc.) to a Microsoft account across devices.",
    whyItMayHelp: "One less background sync process — most useful if this PC is dedicated to gaming rather than shared across your devices.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: true,
  },
  {
    id: "disable-bing-search",
    name: "Disable Web Results in Search",
    category: "privacy-noise",
    whatItChanges: "Stops Start Menu / taskbar search from sending your queries to Bing and mixing in web results, news, and trending stories.",
    whyItMayHelp: "Local search feels faster without waiting on a network round-trip, and keystrokes typed into search stop leaving this PC.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: true,
  },
  {
    id: "disable-suggestions-spotlight",
    name: "Disable Suggestions & Spotlight Nudges",
    category: "privacy-noise",
    whatItChanges: 'Turns off Start Menu app suggestions, lock screen Spotlight tips, "silently installed" recommended apps, and system-pane suggestion nudges.',
    whyItMayHelp:
      'This is where "suggested" Start Menu tiles and unsolicited app installs come from — turning it off keeps the Start Menu to what you actually put there.',
    riskLevel: "low",
    requiresRestart: false,
    reversible: true,
  },
  {
    id: "suppress-security-notifications",
    name: "Suppress Security Toast Popups",
    category: "privacy-noise",
    whatItChanges:
      "Suppresses the Action Center toast popup Windows Security shows for routine status updates. This only hides that popup — Defender's real-time protection, antispyware engine, and cloud sample submission are completely untouched, and the Windows Security app still shows full status when you open it.",
    whyItMayHelp: 'A toast popup stealing focus mid-match for a routine "you\'re protected" status update is pure interruption with zero security benefit.',
    riskLevel: "low",
    requiresRestart: false,
    reversible: true,
  },
  {
    id: "defer-feature-updates",
    name: "Defer Feature Updates",
    category: "privacy-noise",
    whatItChanges: "Pushes big Windows feature upgrades (not security updates) back by up to a year, so they don't land unannounced.",
    whyItMayHelp:
      "A feature upgrade installing itself and rebooting mid-session is one of the worst-timed interruptions Windows can pick — this keeps them on a predictable, delayed schedule instead. Security and quality updates are not affected or delayed by this tweak. Microsoft has scaled back this control on Home/Pro editions in some recent releases, so its effect can vary by edition and build.",
    riskLevel: "low",
    requiresRestart: false,
    reversible: true,
  },
];

export function tweaksByCategory() {
  return CATEGORY_ORDER.map((category) => ({
    category,
    label: CATEGORY_LABELS[category],
    tweaks: TWEAKS.filter((t) => t.category === category),
  })).filter((group) => group.tweaks.length > 0);
}
